/**
 * `ItemList` JSON-LD (structured-data task, 2026-09-27) for browse hubs and
 * `?preset=` pages: the current page of SSR'd result rows, so an AI crawler
 * -- which never runs the search block's own JS -- can read the list as
 * structured data instead of parsing `<li>` markup.
 *
 * Reads the SAME redux state the search block itself was seeded from server
 * side (`state.querystringsearch.subrequests[id].items` -- see
 * `getSearchBlockAsyncData.js` / `reducers/querystringsearch.js`), so this
 * can never list a row the page does not also render. Bounded to the
 * current page only: `items` here is already the one page of results the
 * block fetched, never the full container.
 *
 * Mounted by `withPresetData.jsx`, beside BrowseSEO, so it exists exactly on
 * the pages that carry the browse head.
 */
import React from 'react';
import { useSelector } from 'react-redux';
import { Helmet, flattenToAppURL } from '@plone/volto/helpers';
import { absoluteUrl, presetHref } from './browseState';

/**
 * `{site, pathname, preset, items}` -> an `ItemList` of the rows on THIS
 * page. `items` come straight off the search block's own subrequest, each
 * carrying `@id` and `title` the way the result list itself renders them.
 *
 * Null with no site origin or no rows: an empty result page has nothing to
 * list, and an `ItemList` with zero elements is not useful to a crawler.
 */
export const browseItemListJsonLd = ({ site, pathname, preset, items }) => {
  const origin = (site?.baseUrl || '').replace(/\/+$/, '');
  if (!origin || !Array.isArray(items) || items.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    url: absoluteUrl(site, presetHref(pathname || '/', preset?.id || null)),
    name: preset?.label || undefined,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: absoluteUrl(site, flattenToAppURL(item['@id'])),
      name: item.title || undefined,
    })),
  };
};

const BrowseJsonLd = ({ site, id, pathname, preset }) => {
  const items = useSelector(
    (state) => state.querystringsearch?.subrequests?.[id]?.items,
  );
  const jsonld = browseItemListJsonLd({ site, pathname, preset, items });
  if (!jsonld) return null;
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonld)}</script>
    </Helmet>
  );
};

export default BrowseJsonLd;
